// Third-party libraries
import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import { timeParse } from "d3-time-format";
import { scaleTime, scaleLinear } from "d3-scale";
import { area, line, curveMonotoneX } from "d3-shape";
import { axisBottom, axisLeft, axisRight } from "d3-axis";
import { min, max, extent } from "d3-array";
import { select, selectAll } from "d3-selection";
import { easeLinear } from "d3-ease";
import {
  annotation,
  annotationLabel,
  annotationCalloutElbow,
} from "d3-svg-annotation";
import domtoimage from "dom-to-image";

// Project dependancies
import variables from "../styleVariables";
import { debounce } from "../utilities";

class PlayerChart extends PureComponent {
  constructor(props) {
    super(props);
    this.scoreHistory = React.createRef();
  }

  // Month is 0-indexed. 0 => January, 1 => February, etc.
  milestones = [
    { name: "CX World Championships", date: new Date(2023, 1, 4) },
    { name: "Milan-San Remo", date: new Date(2023, 2, 18) },
    { name: "Tour of Flanders", date: new Date(2023, 3, 2) },
    { name: "Paris-Roubaix", date: new Date(2023, 3, 9) },
    { name: "Liège-Bastogne-Liège", date: new Date(2023, 3, 23) },
    // { name: "Giro d’Italia start", date: new Date(2021, 9, 3) },
    { name: "Giro d’Italia finish", date: new Date(2023, 4, 28) },
    // { name: "Tour de France start", date: new Date(2021, 7,29) },
    { name: "Tour de France finish", date: new Date(2023, 6, 23) },
    // { name: "Vuelta a España start", date: new Date(2021, 9, 20) },
    { name: "Vuelta a España finish", date: new Date(2023, 8, 17) },
    { name: "World Championships", date: new Date(2023, 7, 13) },
  ];

  componentDidMount() {
    const delayedMakeChart = debounce(() => {
      this.drawChart();
    }, 100);
    window.addEventListener("resize", delayedMakeChart);

    this.drawChart();

    if (this.props.makePNG) {
      domtoimage
        .toPng(document.getElementById("score-history"), {
          bgcolor: "#ffffff",
        })
        .then(function (dataUrl) {
          var img = new Image();
          img.setAttribute("id", "score-history-png");
          img.src = dataUrl;
          document.body.appendChild(img);
        });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.handle !== prevProps.handle) {
      this.drawChart();
    }
  }

  drawChart() {
    // Format the dates

    const history = this.props.history;
    const outerWidth = this.scoreHistory.current.getBoundingClientRect().width;
    const outerHeight =
      this.scoreHistory.current.getBoundingClientRect().height;
    const parseTime = timeParse("%Y-%m-%d");
    history.forEach(function (d) {
      if (!(d.day instanceof Date)) {
        d.day = parseTime(d.day);
      }
    });
    // Remove any existing chartage
    selectAll("svg.score-history > *").remove();

    var margin = this.props.makePNG
        ? { top: 10, right: 40, bottom: 30, left: 60 }
        : { top: 5, right: 30, bottom: 20, left: 50 },
      width = outerWidth - margin.left - margin.right,
      height = outerHeight - margin.top - margin.bottom;

    const nearest500 = (number) => (Math.floor(number / 500) + 1) * 500;
    const dateRange = extent(history, (d) => d.day);
    // set the ranges
    var x = scaleTime().range([0, width]).domain(dateRange);
    var y = scaleLinear()
      .range([height, 0])
      .domain([
        min(history, (d) => (d.netpoints < 0 ? d.netpoints : 0)),
        nearest500(
          this.props.overall_max > max(history, (d) => d.netpoints)
            ? this.props.overall_max
            : max(history, (d) => d.netpoints)
        ),
      ]);

    var yRank = scaleLinear().range([height, 0]).domain([0, 100]);

    var scoreLine = line()
      .x((d) => x(d.day))
      .y((d) => y(d.netpoints))
      .curve(curveMonotoneX); // apply smoothing to the line;

    var scoreArea = area()
      .x((d) => x(d.day))
      .y0(y(0))
      .y1((d) => y(d.netpoints))
      .curve(curveMonotoneX); // apply smoothing to the lined;
    var scoreAreaNegative = area()
      .x((d) => x(d.day))
      .y0((d) => y(d.netpoints < 0 ? d.netpoints : 0))
      .y1(y(0))
      .curve(curveMonotoneX); // apply smoothing to the lined;

    var rankLine = line()
      .x((d) => x(d.day))
      .y((d) => yRank(d.percentile))
      .curve(curveMonotoneX); // apply smoothing to the line

    var svg = select("svg.score-history")
      // .attr("width", width + margin.left + margin.right)
      // .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

    // Add the rank area
    svg
      .append("path")
      .data([history])
      .attr("class", "area area--secondary-lighter")
      .attr("d", scoreArea);
    svg
      .append("path")
      .data([history])
      .attr("class", "area area--cancel")
      .attr("d", scoreAreaNegative);

    // Add the rankLine path.
    svg
      .append("path")
      .data([history])
      .attr("class", "line line--secondary")
      .attr("stroke-width", "2")
      .attr("d", scoreLine);

    // Add the scoreLine path.
    svg
      .append("path")
      .data([history])
      .attr("class", "line line--primary")
      .attr("stroke-width", "2")
      .style("stroke-dasharray", "4,2")
      .attr("d", rankLine);

    // Add the X Axis
    svg
      .append("g")
      .attr("transform", `translate(0,${height})`) //"translate(0," + height + ")")
      .attr("class", "axis")
      .call(axisBottom(x).ticks(4));

    // Add the points Axis
    svg.append("g").attr("class", "axis").call(axisLeft(y).ticks(5));
    // Add the rank Axis
    svg
      .append("g")
      .attr("class", "axis")
      .attr("transform", `translate(${width},0)`)
      .call(axisRight(yRank).ticks(2));

    if (min(history, (d) => d.netpoints) < 0) {
      svg
        .append("line")
        .attr("class", "line line--dotted")
        .attr("x1", 0)
        .attr("y1", y(0))
        .attr("x2", width)
        .attr("y2", y(0))
        .attr("stroke", variables.colorRule)
        .style("stroke-width", 1);
    }

    this.milestones.map((milestone, i) => {
      // Set the lowest point of the axes. 0, unless lowest score is negative.
      const minScore =
        min(history, (d) => d.netpoints) < 0
          ? min(history, (d) => d.netpoints)
          : 0;

      if (milestone.date >= dateRange[0] && milestone.date <= dateRange[1]) {
        svg
          .append("line")
          .attr("id", "milestone__thick-axis--" + i)
          .attr(
            "class",
            "line milestone__thick-axis milestone__thick-axis--primary "
          )
          .attr("x1", x(milestone.date))
          .attr("y1", 0)
          .attr("x2", x(milestone.date))
          .attr("y2", y(minScore))
          .attr("stroke", variables.colorPrimary)
          .attr("opacity", 0.1)
          .style("stroke-width", 10)
          .on("mouseover", () => {
            const annotations = [
              {
                note: {
                  title: milestone.name,
                },
                connector: {
                  end: "dot", // 'dot' also available
                  type: annotationCalloutElbow,
                  endScale: 0.25,
                },
                x: x(milestone.date),
                y: 10,
                dy: 10,
                dx: x(milestone.date) < width / 2 ? 40 : -40,
                color: variables.colorCancel,
              },
            ];
            const makeAnnotations = annotation()
              .type(annotationLabel)
              .notePadding(5)
              .annotations(annotations);
            svg
              .append("g")
              .attr("class", "annotation-group")
              .call(makeAnnotations);

            // Reveal the red milestone axis corresponding to current hover, hide all others
            select("#milestone__thin-axis--hover-" + i).attr("opacity", 0.5);
            select("#milestone__thick-axis--hover-" + i).attr("opacity", 0.1);
            select("#milestone__thin-axis-" + i).attr("opacity", 0);
            select("#milestone__thick-axis-" + i).attr("opacity", 0);

            selectAll(".milestone__thin-axis--primary")
              .transition()
              .duration(100)
              .ease(easeLinear)
              .attr("opacity", 0.1);
            selectAll(".milestone__thick-axis--primary")
              .transition()
              .duration(100)
              .ease(easeLinear)
              .attr("opacity", 0.05);
          })
          .on("mouseout", () => {
            // Reveal the basic milestone axes
            select("#milestone__thin-axis--hover-" + i).attr("opacity", 0);
            select("#milestone__thick-axis--hover-" + i).attr("opacity", 0);
            selectAll(".milestone__thin-axis--primary")
              .transition()
              .duration(100)
              .ease(easeLinear)
              .attr("opacity", 0.5);
            selectAll(".milestone__thick-axis--primary")
              .transition()
              .duration(100)
              .ease(easeLinear)
              .attr("opacity", 0.1);
            selectAll(".annotation-group").remove();
          });

        svg
          .append("line")
          .attr("id", "milestone__thin-axis--" + i)
          .attr(
            "class",
            "line milestone__thin-axis milestone__thin-axis--primary cursor-pass-through"
          )
          .attr("x1", x(milestone.date))
          .attr("y1", 0)
          .attr("x2", x(milestone.date))
          .attr("y2", y(minScore))
          .attr("stroke", variables.colorPrimary)
          .attr("opacity", 0.5)
          .style("stroke-width", 1);

        svg
          .append("line")
          .attr("id", "milestone__thick-axis--hover-" + i)
          .attr("class", "line milestone__thick-axis cursor-pass-through")
          .attr("x1", x(milestone.date))
          .attr("y1", 0)
          .attr("x2", x(milestone.date))
          .attr("y2", y(minScore))
          .attr("stroke", variables.colorCancel)
          .attr("opacity", 0)
          .style("stroke-width", 10);

        svg
          .append("line")
          .attr("id", "milestone__thin-axis--hover-" + i)
          .attr("class", "line milestone__thin-axis  cursor-pass-through")
          .attr("x1", x(milestone.date))
          .attr("y1", 0)
          .attr("x2", x(milestone.date))
          .attr("y2", y(minScore))
          .attr("stroke", variables.colorCancel)
          .attr("opacity", 0)
          .style("stroke-width", 1);
      }
    });
  }

  render() {
    return (
      <div
        id="score-history"
        className={`${this.props.makePNG && "score-history--makePNG"}`}
      >
        <div className="legend legend--horizontal">
          <div className="legend-item legend-item--secondary-lighter legend-item--border-secondary">
            Points
          </div>
          <div className="legend-item legend-item--primary legend-item--line">
            Ranking percentile
          </div>
        </div>
        <svg
          className={"score-history chart"}
          ref={this.scoreHistory}
          width="100%"
          height={this.props.height || 150}
        />
      </div>
    );
  }
}

PlayerChart.propTypes = {
  history: PropTypes.array.isRequired,
  handle: PropTypes.string.isRequired,
  makePNG: PropTypes.bool,
  overall_max: PropTypes.number,
};

export default PlayerChart;
